import { Component } from '@angular/core';
import { AuthService } from '../../../../app/components/shared/services/auth.service'
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { SetupService } from '../../shared/services/setup.service';
import { HttpClientModule } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';


@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [CommonModule, FormsModule,RouterModule,],
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.css']
})
export class LoginFormComponent {
  loading = false;
  formData: any = {};
  errorMessage: string | null = null;
  isLoggedIn = false;
  isSubscriptionError = false;
  isTenantDeactivated = false;

  ngOnInit() {
    // Check for success message from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const renewalSuccess = urlParams.get('renewal') === 'success';
    const email = urlParams.get('email');

    // Clear the URL parameters to prevent showing the message again on refresh
    if (renewalSuccess) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      this.errorMessage = 'Subscription renewed successfully! Please log in again.';
      this.isSubscriptionError = false;

      // Auto-fill the email if provided
      if (email) {
        this.formData.email = decodeURIComponent(email);
      }
    } else {
      // Check for success message from navigation state (fallback)
      const navigation = this.router.getCurrentNavigation();
      const state = navigation?.extras.state as { showRenewalSuccess?: boolean, email?: string };

      if (state?.showRenewalSuccess) {
        this.errorMessage = 'Subscription renewed successfully! Please log in again.';
        this.isSubscriptionError = false;

        // Auto-fill the email if provided
        if (state.email) {
          this.formData.email = state.email;
        }
      } else {
        // Default behavior - load remembered email
        this.formData.email = localStorage.getItem('rememberedEmail') || '';
      }
    }

    this.formData.rememberMe = !!this.formData.email;
  }
  constructor(private authService: AuthService, private router: Router, private setupService: SetupService) { }
// In thy component.ts
showPassword: boolean = false;

togglePasswordVisibility(): void {
  this.showPassword = !this.showPassword;
}

  onSubmit(event: Event) {
    event.preventDefault();
    this.loading = true;
    this.errorMessage = null;
    this.isSubscriptionError = false;
    this.isTenantDeactivated = false;

    this.authService.logIn(this.formData.email, this.formData.password, this.formData.rememberMe).subscribe({
      next: (response) => {
        this.isLoggedIn = true;
        this.loading = false;

        // Check subscription status from JWT token
        const token = this.authService.getToken();
        if (token) {
          try {
            const decodedToken: any = jwtDecode(token);
            // If subscription status is explicitly set to false in the token
            if (decodedToken.SubscriptionStatus === false || decodedToken.SubscriptionStatus === 'false') {
              this.isSubscriptionError = true;
              this.errorMessage = 'Your subscription is not active. Please renew to continue using the service.';
              return; // Stop further navigation
            }

          } catch (error) {
// $&
          }
        }

        // Force a fresh check with the server for setup status
        this.setupService.checkSetupStatus(true).subscribe({
          next: (setupResponse) => {
// $&
            if (setupResponse.isSetupComplete) {
              const accountType = this.authService.getAccountType();
              this.router.navigate([accountType === 'calculator' ? '/calc' : '/dashboard']);
            } else {
              this.router.navigate(['/business-details']);
            }
          },
          error: (setupError) => {
// $&
            // Default to business details page if there's an error checking setup status
            this.router.navigate(['/business-details']);
          }
        });
      },
      error: (error) => {
        this.loading = false;

        // Handle different types of errors
        if (error && error.error) {
          // Handle error response from the server
          if (error.error.message) {
            this.errorMessage = error.error.message;
          } else if (typeof error.error === 'string') {
            // In case the error is a plain string
            this.errorMessage = error.error;
          } else {
            this.errorMessage = 'An error occurred during login. Please try again.';
          }

          // Check the type of error
          const errorMessageLower = this.errorMessage?.toLowerCase() || '';
          this.isTenantDeactivated = errorMessageLower.includes('deactivated');

          // Check for specific subscription-related errors that should show the renewal button
          const showRenewalButton =
            errorMessageLower.includes('expired') ||
            errorMessageLower.includes('inactive') ||
            errorMessageLower.includes('deactivated') ||
            errorMessageLower.includes('payment required') ||
            errorMessageLower.includes('subscription is not active') ||
            errorMessageLower.includes('no subscription found') ||
            errorMessageLower.includes('grace period') ||
            errorMessageLower.includes('cancelled') ||
            errorMessageLower.includes('canceled') ||
            // Be robust to provider/API phrasing
            errorMessageLower.includes('paystack') ||
            errorMessageLower.includes('failed to fetch subscription') ||
            errorMessageLower.includes('provider');


          // Set subscription error flag for specific renewal-required errors
          this.isSubscriptionError =
            (error.status === 400 || error.status === 402 || this.isTenantDeactivated) &&
            showRenewalButton &&
            !errorMessageLower.includes('already active');
        } else {
          // Handle network or other client-side errors
          this.errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
          this.isSubscriptionError = false;
          this.isTenantDeactivated = false;
        }
      },
    });

    if (this.formData.rememberMe) {
      localStorage.setItem('rememberedEmail', this.formData.email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
  }

  renewSubscription(): void {
    // Get subscription details from the error response if available
    const subscriptionDetails = this.errorMessage?.includes('subscription') ?
      this.extractSubscriptionDetails(this.errorMessage) : null;
// $&
    // Navigate to the renewal page with the current email, password, and subscription details
    this.router.navigate(['/renewal'], {
      state: {
        email: this.formData.email,
        password: this.formData.password, // Include the password in the state
        subscriptionCode: subscriptionDetails?.code,
        emailToken: subscriptionDetails?.token,
        isPaystack: subscriptionDetails?.isPaystack || false
      }
    });

  }

  /**
   * Extracts subscription details from error message if available
   * This is a helper method to parse subscription details that might be included in the error message
   */
  private extractSubscriptionDetails(errorMessage: string): { code?: string; token?: string; isPaystack: boolean } | null {
    if (!errorMessage) return null;

    try {
      // Check if the error message contains subscription details
      const match = errorMessage.match(/subscription[\s\S]*?code[\s:]+([^\s,]+)[\s\S]*?token[\s:]+([^\s,]+)/i);
      if (match && match.length >= 3) {
        return {
          code: match[1],
          token: match[2],
          isPaystack: errorMessage.toLowerCase().includes('paystack')
        };
      }

      // Try to parse as JSON if it looks like a JSON string
      if (errorMessage.trim().startsWith('{') && errorMessage.trim().endsWith('}')) {
        const errorObj = JSON.parse(errorMessage);
        if (errorObj.subscription) {
          return {
            code: errorObj.subscription.paystackSubscriptionCode,
            token: errorObj.subscription.paystackEmailToken,
            isPaystack: true
          };
        }
      }
    } catch (e) {
// $&
    }

    return { isPaystack: errorMessage.toLowerCase().includes('paystack') };
  }
}
